import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { LoginSuccessfulResponse, PermissionAssignmentModel } from '@/api/model'

interface AuthState {
  csrfToken: string | null
  role: string | null
  // Company the token currently acts on. For a company management user this is simply their
  // own company; for an Administrator it is the customer portal they switched into, and null
  // until they pick one. The client cannot read the claim out of the access token, so the
  // backend reports it in the login/refresh/switch response.
  companyId: number | null
  features: string[] | null
  supportedLanguages: string[]
  // Own permission set of the logged-in user — drives all client-side UI gating
  // (sidebar/route/button). Arrives in the login/register/refresh response.
  currentUserPermissions: PermissionAssignmentModel[]
  isAuthenticated: boolean
  setAuth: (response: LoginSuccessfulResponse) => void
  clearAuth: () => void
}

// Access token lives only in memory — never persisted to localStorage
let inMemoryAccessToken: string | null = null

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      csrfToken: null,
      role: null,
      companyId: null,
      features: null,
      supportedLanguages: [],
      currentUserPermissions: [],
      isAuthenticated: false,
      setAuth: (response) => {
        inMemoryAccessToken = response.accessToken ?? null
        set({
          csrfToken: response.csrfToken ?? null,
          role: response.role,
          companyId: response.companyId ?? null,
          features: response.features,
          supportedLanguages: response.supportedLanguages ?? [],
          currentUserPermissions: response.assignedPermissions ?? [],
          isAuthenticated: !!response.accessToken,
        })
      },
      clearAuth: () => {
        inMemoryAccessToken = null
        set({
          csrfToken: null,
          role: null,
          companyId: null,
          features: null,
          supportedLanguages: [],
          currentUserPermissions: [],
          isAuthenticated: false,
        })
      },
    }),
    {
      name: 'auth',
      // Access token is never persisted — only metadata and CSRF token
      partialize: (state) => ({
        csrfToken: state.csrfToken,
        role: state.role,
        companyId: state.companyId,
        features: state.features,
        supportedLanguages: state.supportedLanguages,
        // Persisted so the sidebar/routes don't flicker on reload before the
        // token refresh re-delivers the permission set.
        currentUserPermissions: state.currentUserPermissions,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)

export function getInMemoryAccessToken() {
  return inMemoryAccessToken
}
